import React, { useState } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { formatDateDash } from '../../support/DateTime';
import {
  DatePicker,
  FormField,
  Hoverable,
  Icon,
  Separator,
  Checkbox,
  Text,
  TextInput,
  XButton,
} from '../uiold';
import { themeColors } from '../uiold/Constants';

import { Market } from '../../types/shared/Market';
import {
  Button,
  HStack,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spacer,
  Text as ChakraText,
} from '@chakra-ui/react';

type Props = {
  canEdit: boolean;
  market: Market;
  onSave: (market: Market) => void;
  onClose: () => void;
  onDeletePress: (market: Market) => void;
};

export function MarketEditModalContent(props: Props) {
  const { canEdit, market, onSave, onClose, onDeletePress } = props;

  const [name, setName] = useState(market.name);
  const [description, setDescription] = useState(market.description);
  const [numSpots, setNumSpots] = useState(market.num_spots);
  const [selectedDates, setSelectedDates] = useState(
    () => new Set(market.dates),
  );
  const [hidden, setHidden] = useState(market.hidden);

  const onSubmit = () => {
    const { id, season, map, createdAt, updatedAt } = market;
    onSave({
      id,
      season,
      name,
      description,
      dates: Array.from(selectedDates),
      num_spots: numSpots,
      map,
      hidden,
      createdAt,
      updatedAt,
    });
  };

  const onDatePress = (date: string) => {
    if (selectedDates.has(date)) {
      selectedDates.delete(date);
    } else {
      selectedDates.add(date);
    }
    setSelectedDates(new Set([...selectedDates].sort()));
  };

  return (
    <ModalContent
      as="form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <ModalHeader>
        <ChakraText>
          {canEdit ? t('Edit Market') : t('Market Details')}
        </ChakraText>
      </ModalHeader>
      <ModalCloseButton />
      <ModalBody p={0}>
        <View style={styles.formView}>
          <FormField
            render={(id) => (
              <View style={styles.formField}>
                <Text accessibilityRole="label" htmlFor={id}>
                  {t('Market Name:')}
                </Text>
                <TextInput
                  disabled={!canEdit}
                  id={id}
                  value={name}
                  onChangeText={(value) => setName(value)}
                />
              </View>
            )}
          />
          <FormField
            render={(id) => (
              <View style={styles.formField}>
                <Text accessibilityRole="label" htmlFor={id}>
                  {t('Market Description:')}
                </Text>
                <TextInput
                  id={id}
                  disabled={!canEdit}
                  value={description}
                  onChangeText={(value) => setDescription(value)}
                />
              </View>
            )}
          />
          <View style={styles.formFieldInline}>
            <Checkbox
              isDisabled={!canEdit}
              isChecked={hidden}
              text={t('Hide this market from the signup form')}
              textStyle={{ fontSize: 16 }}
              onChange={(value) => setHidden(value)}
            />
          </View>
          <FormField
            render={(id) => (
              <View style={styles.formFieldInline}>
                <View style={styles.formFieldLabel}>
                  <Text accessibilityRole="label" htmlFor={id}>
                    {t('Number of Stalls:')}
                  </Text>
                </View>
                <TextInput
                  id={id}
                  disabled={!canEdit}
                  keyboardType="numeric"
                  value={numSpots.toString()}
                  style={styles.numSpotsInput}
                  onChangeText={(value) => setNumSpots(Number(value))}
                />
              </View>
            )}
          />
          <FormField
            render={(id) => (
              <View style={styles.formFieldInline}>
                <Text
                  style={styles.formFieldLabel}
                  accessibilityRole="label"
                  htmlFor={id}
                >
                  {t('Number of Dates:')}
                </Text>
                <Text accessibilityRole="label" htmlFor={id}>
                  {selectedDates.size}
                </Text>
              </View>
            )}
          />
          <View style={styles.calendarContainer}>
            <View style={styles.datePickerContainer}>
              <DatePicker
                selectedDates={selectedDates}
                onDateClick={onDatePress}
              />
            </View>
            <View style={styles.selectedDateList}>
              <Text style={styles.datesText}>{t('Dates')}</Text>
              <Separator style={styles.datesSeparator} />
              <ScrollView style={styles.dateList}>
                {Array.from(selectedDates).map((date, i) => {
                  return (
                    <Hoverable
                      key={i}
                      render={(isHovered, eventHandlers) => (
                        <View
                          {...eventHandlers}
                          style={[
                            styles.selectedDatesCard,
                            isHovered && {
                              backgroundColor: themeColors.backgroundBlue,
                            },
                          ]}
                        >
                          <Text style={styles.alignSelfCentered}>
                            {formatDateDash(date, true)}
                          </Text>
                          <View style={styles.XButtonWrap}>
                            {isHovered ? (
                              <TouchableOpacity
                                onPress={() => onDatePress(date)}
                                style={styles.xButton}
                              >
                                <Icon name="remove" size="M" color="red" />
                              </TouchableOpacity>
                            ) : (
                              <XButton color="white" style={styles.xButton} />
                            )}
                          </View>
                        </View>
                      )}
                    />
                  );
                })}
              </ScrollView>
            </View>
          </View>
        </View>
      </ModalBody>
      <ModalFooter>
        <HStack flex={1}>
          {canEdit ? (
            <>
              <Button
                variant="outline"
                colorScheme="red"
                onClick={() => onDeletePress(market)}
              >
                {t('Delete')}
              </Button>
              <Spacer />
              <Button onClick={() => onClose()}>{t('Cancel')}</Button>
              <Button type="submit" variant="primary">
                {t('Save')}
              </Button>
            </>
          ) : (
            <Button onClick={() => onClose()}>{t('Close')}</Button>
          )}
        </HStack>
      </ModalFooter>
    </ModalContent>
  );
}

const styles = StyleSheet.create({
  formView: {
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  formField: {
    marginVertical: 10,
  },
  formFieldInline: {
    flexDirection: 'row',
    paddingVertical: 10,
  },
  formFieldLabel: {
    marginRight: 10,
    justifyContent: 'center',
  },
  numSpotsInput: {
    width: 80,
  },
  datesText: {
    alignSelf: 'center',
  },
  datesSeparator: {
    backgroundColor: themeColors.blue,
    marginVertical: 10,
  },
  calendarContainer: {
    flexDirection: 'row',
    marginVertical: 20,
  },
  selectedDateList: {
    flex: 1,
    minHeight: 380,
    paddingTop: 10,
    borderRadius: 5,
    borderColor: themeColors.borderGrey,
    borderWidth: StyleSheet.hairlineWidth,
  },
  datePickerContainer: {
    flex: 2,
    padding: 10,
    minHeight: 380,
    marginRight: 20,
    borderRadius: 5,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: themeColors.borderGrey,
  },
  dateList: {
    height: 0,
  },
  alignSelfCentered: {
    marginLeft: 40,
    alignSelf: 'center',
  },
  xButton: {
    height: 20,
    marginTop: 0,
    justifyContent: 'center',
  },
  selectedDatesCard: {
    marginBottom: 5,
    flexDirection: 'row',
  },
  XButtonWrap: {
    maxHeight: 20,
    marginLeft: 10,
    marginRight: 5,
  },
});
